/**
 * Публичные тарифы ЦБР, SLA и тексты страницы /cbr.
 * Расчёт COGS: docs/CBR_PRICING.md
 */

export const CBR_EMPLOYER_MULTIPLIER = 1.076;
export const CBR_TARGET_GROSS_MARGIN = 0.5;

export const CBR_SALARY_GROSS = {
  manager: 95_000,
  aiEngineer: 200_000,
  mlNlp: 240_000,
  devopsApi: 220_000,
};

/** Лимиты для договора и страницы */
export const CBR_SLA = {
  start: {
    consultHoursPerMonth: 10,
    ticketResponseHours: 8,
    channels: 'тикет-система, почта или мессенджер',
    monitoring: 'по запросу',
  },
  ml: {
    consultHoursPerMonth: 10,
    ticketResponseHours: 6,
    reportCadence: 'ежемесячно',
    channels: 'тикет-система, почта или мессенджер',
    monitoring: 'ежемесячный отчёт по LLM',
  },
  operations: {
    consultHoursPerMonth: 10,
    ticketResponseHours: 4,
    channels: 'тикет-система, почта или мессенджер',
    monitoring: 'рабочие дни, без круглосуточного дежурства',
  },
  pro: {
    consultHoursPerMonth: 'по согласованию',
    ticketResponseHours: 2,
    channels: 'по согласованию с менеджером',
    monitoring: 'ежедневный мониторинг LLM и API',
    teamFte: '3,5 FTE',
  },
};

const sla = CBR_SLA;

export const CBR_TIERS = [
  {
    id: 'start',
    price: 99_000,
    priceLabel: '99 000',
    name: 'Старт',
    tagline: 'Базовый минимум',
    highlight: false,
    forWhom: 'Поддержка сотрудников и удалённая помощь.',
    includes: [
      'Тикеты, почта, мессенджер',
      `Менеджер и AI-инженер, до ${sla.start.consultHoursPerMonth} ч консультаций`,
      `Ответ до ${sla.start.ticketResponseHours} рабочих часов`,
    ],
  },
  {
    id: 'ml-analytics',
    price: 279_000,
    priceLabel: '279 000',
    name: 'ML-аналитика',
    tagline: 'Контроль затрат на LLM',
    highlight: false,
    forWhom: 'Прозрачность расходов на LLM в проде.',
    includes: [
      'Всё из Старт',
      'Ежемесячный отчёт и рекомендации по оптимизации',
      `Ответ до ${sla.ml.ticketResponseHours} рабочих часов`,
    ],
  },
  {
    id: 'operations',
    price: 490_000,
    priceLabel: '490 000',
    name: 'Операции',
    tagline: 'Мониторинг и инфраструктура',
    highlight: false,
    forWhom: 'Правки в проде и API без полной команды.',
    includes: [
      'Всё из ML-аналитика',
      'ML/NLP и DevOps по 0,5 FTE, мониторинг в рабочие дни',
      `Ответ до ${sla.operations.ticketResponseHours} рабочих часов`,
    ],
  },
  {
    id: 'cbr-pro',
    price: 1_990_000,
    priceLabel: '1 990 000',
    name: 'ЦБР Профессиональный',
    tagline: 'Выделенная команда',
    highlight: true,
    forWhom: 'Критичные AI-сервисы, отдельная команда на клиента.',
    includes: [
      `Команда ${sla.pro.teamFte}: менеджер, ML/NLP, DevOps, AI-инженер`,
      'Ежедневный мониторинг LLM и API',
      `Ответ до ${sla.pro.ticketResponseHours} ч, ночь и выходные - в договоре`,
    ],
  },
];

const tier = (id) => CBR_TIERS.find((t) => t.id === id);

/** Тексты страницы /cbr (единый источник с тарифами и SLA) */
export const CBR_PAGE = {
  hero: {
    eyebrow: 'Техническая поддержка AI-сервисов',
    title: 'ЦБР - центр быстрого реагирования',
    lead:
      'Поддержка корпоративных LLM и API: от тарифа Старт с тикетами и консультациями до выделенной команды с ежедневным мониторингом.',
    sub:
      `Четыре тарифа по нарастающей - от ${tier('start').priceLabel} до ${tier('cbr-pro').priceLabel} ₽/мес для юрлиц, без НДС. Лимиты часов, SLA и каналы связи фиксируем в договоре.`,
    ctaPrimary: 'Обсудить тариф',
    ctaSecondary: 'Заказать звонок',
  },
  tariffs: {
    title: 'Тарифы',
    intro: `Цены в рублях в месяц, без НДС. На Старте - до ${sla.start.consultHoursPerMonth} ч консультаций и ответ до ${sla.start.ticketResponseHours} рабочих часов. Операции (${tier('operations').priceLabel} ₽) - между ML-аналитикой и выделенной командой ЦБР.`,
    footnote:
      'Каналы связи, сверхлимит консультаций и регламент дежурств - в приложении к договору. Счёт с НДС 22% - отдельно.',
    enterpriseBadge: 'Enterprise',
    ctaTemplate: 'Выбрать',
  },
  comparison: {
    title: 'Сравнение по SLA',
    intro: 'Рабочие часы - по московскому времени, если в договоре не указано иное.',
    headers: ['Тариф', 'Цена', 'Консультации', 'Ответ на тикет', 'Мониторинг'],
  },
  startSection: {
    title: `Тариф ${tier('start').name}: как устроена работа`,
    intro: `Базовый минимум (${tier('start').priceLabel} ₽/мес). Без выделенного NOC - пишете в привычный канал, подключаемся удалённо.`,
    blocks: [
      {
        title: 'Тикеты, почта, мессенджер',
        text: `Единый вход: ${sla.start.channels}. Подстраиваемся под ваш процесс, без обязательного нового портала.`,
      },
      {
        title: `До ${sla.start.consultHoursPerMonth} часов в месяц`,
        text: 'Онлайн-консультации с менеджером и AI-инженером: сценарии, настройки, ответы сотрудникам. Сверх лимита - по соглашению.',
      },
      {
        title: 'Удалённо на вашей машине',
        text: 'Подключение, настройка и сопровождение - по политике доступа и согласованию с ИБ.',
      },
    ],
  },
  operationsSection: {
    title: `Тариф ${tier('operations').name}`,
    intro: `${tier('operations').priceLabel} ₽/мес. ${sla.operations.monitoring}.`,
    blocks: [
      {
        title: 'ML и инфраструктура',
        text: 'Половина ставки ML/NLP и половина DevOps/API на ваш контур - регулярные правки без полной четвёрки.',
      },
      {
        title: `Ответ до ${sla.operations.ticketResponseHours} часов`,
        text: 'Быстрее, чем на Старте и ML, но без круглосуточного дежурства.',
      },
      {
        title: 'Когда переходить на Профессиональный',
        text: `Нужны ${sla.pro.monitoring} и ${sla.pro.teamFte} только на вас - от ${tier('cbr-pro').priceLabel} ₽/мес.`,
      },
    ],
  },
  proSection: {
    title: `Выделенная команда: ${tier('cbr-pro').name}`,
    intro: `${tier('cbr-pro').priceLabel} ₽/мес, команда ${sla.pro.teamFte}. ${sla.pro.monitoring}. Ответ до ${sla.pro.ticketResponseHours} рабочих часов.`,
    blocks: [
      {
        title: 'Быстрое реагирование',
        text: 'Инциденты и всплески расхода токенов - в операционном окне, не в конце отчётного месяца.',
      },
      {
        title: 'ML и NLP (1,0 FTE)',
        text: 'Модели, промпты, пайплайны и отчёты под ваши KPI по затратам на LLM.',
      },
      {
        title: 'Менеджер (1,0 FTE)',
        text: 'Единая точка контакта: задачи с бизнес-языка на технический без потери смысла.',
      },
      {
        title: 'DevOps/API и AI-инженер',
        text: 'Серверы, интеграции, API и ежедневные операции - отключение «прожорливых» сценариев.',
      },
    ],
  },
  faq: {
    title: 'Коротко о формате',
  },
};

export const CBR_FAQ = [
  {
    q: 'Чем ЦБР отличается от обычной техподдержки?',
    a: `Фокус на AI: LLM, API, токены и стабильность в проде. На ${tier('start').name} - ${sla.start.channels} и до ${sla.start.consultHoursPerMonth} ч консультаций. На ${tier('cbr-pro').name} - ${sla.pro.teamFte} и ${sla.pro.monitoring}.`,
  },
  {
    q: 'С какого тарифа начать?',
    a: `${tier('start').name} от ${tier('start').priceLabel} ₽/мес. Для отчётов по LLM - ${tier('ml-analytics').name} от ${tier('ml-analytics').priceLabel} ₽. Между ML и полной командой - ${tier('operations').name} от ${tier('operations').priceLabel} ₽.`,
  },
  {
    q: 'Что входит в лимиты по часам?',
    a: `На ${tier('start').name} и ${tier('ml-analytics').name} - до ${sla.start.consultHoursPerMonth} ч онлайн-консультаций в месяц, сверх - по соглашению. На ${tier('cbr-pro').name} объём консультаций ${sla.pro.consultHoursPerMonth}. Ночь и выходные - только Профессиональный, в договоре.`,
  },
  {
    q: `Чем ${tier('cbr-pro').name} отличается от ${tier('operations').name}?`,
    a: `${tier('cbr-pro').name}: ${sla.pro.teamFte} на одного клиента, ${sla.pro.monitoring}, ответ до ${sla.pro.ticketResponseHours} ч. ${tier('operations').name}: ${sla.operations.monitoring}, ответ до ${sla.operations.ticketResponseHours} ч, без 24/7.`,
  },
  {
    q: 'Цены указаны с НДС?',
    a: 'Нет, все суммы на странице - без НДС для юридических лиц. Счёт с НДС 22% выставляем отдельно, если это требуется по договору.',
  },
];

/** Строки таблицы сравнения SLA */
export const CBR_SLA_COMPARISON_ROWS = CBR_TIERS.map((t) => {
  const key = t.id === 'start' ? 'start' : t.id === 'ml-analytics' ? 'ml' : t.id === 'operations' ? 'operations' : 'pro';
  const s = CBR_SLA[key];
  const consult =
    typeof s.consultHoursPerMonth === 'number'
      ? `до ${s.consultHoursPerMonth} ч/мес`
      : s.consultHoursPerMonth;
  return {
    tierId: t.id,
    name: t.name,
    priceLabel: t.priceLabel,
    consult,
    ticket: `до ${s.ticketResponseHours} ч`,
    monitoring: s.monitoring || s.reportCadence || '-',
  };
});

/** Упрощённый расчёт COGS по FTE (для тестов и пересчёта) */
export function estimateTierCogs(fteByRole, { tools = 0, onCallPremium = 0 } = {}) {
  const salaries = CBR_SALARY_GROSS;
  const mult = CBR_EMPLOYER_MULTIPLIER;
  let fot = 0;
  if (fteByRole.manager) fot += salaries.manager * fteByRole.manager * mult;
  if (fteByRole.aiEngineer) fot += salaries.aiEngineer * fteByRole.aiEngineer * mult;
  if (fteByRole.mlNlp) fot += salaries.mlNlp * fteByRole.mlNlp * mult;
  if (fteByRole.devopsApi) fot += salaries.devopsApi * fteByRole.devopsApi * mult;
  fot *= 1 + onCallPremium;
  const subtotal = fot + tools;
  return Math.round(subtotal * 1.12);
}

export function priceAtTargetMargin(cogs) {
  return Math.round(cogs / (1 - CBR_TARGET_GROSS_MARGIN));
}
